// Module 4 / Slice 5b — Workforce employee detail screen.
//
// Read-only profile header + tabbed sub-views (employment records /
// assignments / dimension tags). The route param employeeId is supplied
// by TanStack Router's /admin/workforce/employees/$employeeId.
//
// Write actions (create/update/archive) are deferred to a Phase-4
// follow-up (drawer-style edit). The detail screen ships read-first to
// match M3 conventions; the API contract carries the full write surface.
//
// PATTERNS.md §2.2 (screen composition with tabbed sub-views).

import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { workforceApiClient } from "../../api/client";
import { useI18n } from "../../i18n/I18nContext";
import { EmploymentRecordsTab } from "./tabs/EmploymentRecordsTab";
import { AssignmentsTab } from "./tabs/AssignmentsTab";
import { DimensionTagsTab } from "./tabs/DimensionTagsTab";

type TabKey = "employment" | "assignments" | "dimension_tags";

export function EmployeeDetailScreen(): JSX.Element {
  const { t } = useI18n();
  const { employeeId } = useParams({
    from: "/authed/admin/workforce/employees/$employeeId",
  }) as { employeeId: string };
  const [tab, setTab] = useState<TabKey>("employment");

  const query = useQuery({
    queryKey: ["workforce", "employee", employeeId] as const,
    queryFn: async () => {
      const response = await workforceApiClient.GET(
        "/workforce/employees/{employeeId}",
        { params: { path: { employeeId } } },
      );
      if (response.response.status === 404) {
        return null;
      }
      if (!response.data) {
        throw new Error("workforce_employee_fetch_failed");
      }
      return response.data;
    },
  });

  return (
    <div>
      <h1>{t("workforce.employee.detail.heading")}</h1>

      {query.isLoading && <p>{t("login.discovering")}</p>}
      {query.isError && (
        <p role="alert" style={{ color: "var(--rc-color-danger)" }}>
          {(query.error as Error).message}
        </p>
      )}
      {query.data === null && (
        <p data-testid="employee-not-found">
          {t("workforce.employee.detail.notFound")}
        </p>
      )}
      {query.data && (
        <>
          <dl className="rc-detail" data-testid="employee-header">
            <dt>{t("workforce.employee.detail.field.employeeNumber")}</dt>
            <dd>
              <code data-testid="employee-detail-employeeNumber">
                {query.data.employeeNumber}
              </code>
            </dd>
            <dt>{t("workforce.employee.detail.field.displayName")}</dt>
            <dd data-testid="employee-detail-displayName">
              {query.data.displayName}
            </dd>
            <dt>{t("workforce.employee.detail.field.status")}</dt>
            <dd data-testid="employee-detail-status">
              {query.data.employmentStatus}
            </dd>
            <dt>{t("workforce.employee.detail.field.hireDate")}</dt>
            <dd>{query.data.hireDate ?? "\u2014"}</dd>
            <dt>{t("workforce.employee.detail.field.terminationDate")}</dt>
            <dd>{query.data.terminationDate ?? "\u2014"}</dd>
            <dt>{t("workforce.employee.detail.field.email")}</dt>
            <dd>{query.data.email ?? "\u2014"}</dd>
            <dt>{t("workforce.employee.detail.field.idpSubject")}</dt>
            <dd>
              {query.data.idpSubject ? (
                <code>{query.data.idpSubject}</code>
              ) : (
                "\u2014"
              )}
            </dd>
          </dl>

          <nav className="rc-tabs" data-testid="employee-detail-tabs">
            <button
              type="button"
              data-testid="employee-tab-employment"
              aria-pressed={tab === "employment"}
              onClick={() => setTab("employment")}
            >
              {t("workforce.employee.detail.tab.employmentRecords")}
            </button>
            <button
              type="button"
              data-testid="employee-tab-assignments"
              aria-pressed={tab === "assignments"}
              onClick={() => setTab("assignments")}
            >
              {t("workforce.employee.detail.tab.assignments")}
            </button>
            <button
              type="button"
              data-testid="employee-tab-dimension-tags"
              aria-pressed={tab === "dimension_tags"}
              onClick={() => setTab("dimension_tags")}
            >
              {t("workforce.employee.detail.tab.dimensionTags")}
            </button>
          </nav>

          <section style={{ marginTop: "1rem" }}>
            {tab === "employment" && (
              <EmploymentRecordsTab employeeId={employeeId} />
            )}
            {tab === "assignments" && <AssignmentsTab employeeId={employeeId} />}
            {tab === "dimension_tags" && (
              <DimensionTagsTab employeeId={employeeId} />
            )}
          </section>
        </>
      )}
    </div>
  );
}
